import { ScreenEnum } from '../../hooks/navigation/Screens';

/**
 * The chats of the platform agent's panel (docs/platform-agent/contracts.md §6, design: Chats, Day line): the chat
 * list in words (who started a chat and when, where it stands), grouped by day; the day lines of a chat that goes on
 * over several days; and how a chat the panel opens continues its eve session. Pure, so it is tested without the panel.
 */

/** Chats per page of the list (§6: 30 a page). */
export const PLATFORM_AGENT_CHATS_PAGE = 30;

/** The list's days, newest first. */
export const PLATFORM_AGENT_CHAT_DAYS = Object.freeze(['today', 'yesterday', 'earlier']);

/** Each §6 status and the tone of its dot (design: Done green, waiting and partly applied amber, failed red). */
export const PLATFORM_AGENT_CHAT_TONES = Object.freeze({
  done: 'success',
  answered: 'neutral',
  waiting: 'warning',
  partial: 'warning',
  rejected: 'neutral',
  not_yet: 'neutral',
  failed: 'danger',
});

/** Who started a chat (§6 `started_by.kind`), as the list and a read-only chat name them. */
export const PLATFORM_AGENT_CHAT_KINDS = Object.freeze(['foodamigos', 'owner', 'staff', 'partner']);

function dateFormat(locale, timeZone, options) {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: timeZone || undefined });
  } catch {
    // An unknown time zone or locale: the browser's own.
    return new Intl.DateTimeFormat(undefined, options);
  }
}

/** The calendar day of `at` in the restaurant's time zone, as YYYY-MM-DD (so days compare as strings). */
export function platformAgentDayKey(at, timeZone) {
  return dateFormat('en-CA', timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(at));
}

function dayBefore(key) {
  const [year, month, day] = key.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10);
}

/** Today, yesterday or earlier, in the restaurant's time zone; a time ahead of `now` (clock skew) is today. */
export function platformAgentDay(at, { now = Date.now(), timeZone } = {}) {
  const key = platformAgentDayKey(at, timeZone);
  const today = platformAgentDayKey(now, timeZone);

  if (key >= today) {
    return 'today';
  }

  return key === dayBefore(today) ? 'yesterday' : 'earlier';
}

/** The time of day, 24-hour as in the design ("15:02"). */
export function platformAgentTime(at, { timeZone, locale } = {}) {
  return dateFormat(locale, timeZone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(at));
}

/** A list row's "when": the time today and yesterday (the day is its group), the date earlier ("22 Oct"). */
export function platformAgentChatTime(at, { now, timeZone, locale } = {}) {
  if (platformAgentDay(at, { now, timeZone }) !== 'earlier') {
    return platformAgentTime(at, { timeZone, locale });
  }

  return dateFormat(locale, timeZone, { day: 'numeric', month: 'short' }).format(new Date(at));
}

/**
 * A day and time in words ("Yesterday, 15:02", "Today, 09:10", "27 October, 18:30"), for day lines and whose chat it
 * is. `t` has the keys `when.today`, `when.yesterday` and `when.date`, with {{time}} (and {{date}}).
 */
export function platformAgentWhen(at, { now, timeZone, locale } = {}, t) {
  const time = platformAgentTime(at, { timeZone, locale });
  const day = platformAgentDay(at, { now, timeZone });

  if (day !== 'earlier') {
    return t(`when.${day}`, { time });
  }

  const date = dateFormat(locale, timeZone, { day: 'numeric', month: 'long' }).format(new Date(at));

  return t('when.date', { date, time });
}

/**
 * The locale for dates in the panel: the restaurant's (§2 `locale`) when it is in the dashboard's language, else the
 * dashboard's language (English as in the design, "22 Oct" and 24-hour times).
 */
export function platformAgentDateLocale(context, language) {
  const locale = context?.locale;

  if (locale && language && locale.toLowerCase().startsWith(language.toLowerCase())) {
    return locale;
  }

  return language === 'en' ? 'en-GB' : language || undefined;
}

/**
 * The chats of every page loaded, once each (a chat that moved up between two pages shows in the first), newest
 * first as the agent sends them.
 */
export function platformAgentChatsOf(pages) {
  const seen = new Set();
  const chats = [];

  (pages ?? []).forEach((page) => {
    (page?.chats ?? []).forEach((chat) => {
      if (chat?.id && !seen.has(chat.id)) {
        seen.add(chat.id);
        chats.push(chat);
      }
    });
  });

  return chats;
}

/** The chats under Today, Yesterday and Earlier (by their last update), leaving out a day without chats. */
export function groupPlatformAgentChats(chats, { now, timeZone } = {}) {
  const days = { today: [], yesterday: [], earlier: [] };

  chats.forEach((chat) => {
    days[platformAgentDay(chat.updated_at ?? chat.created_at, { now, timeZone })].push(chat);
  });

  return PLATFORM_AGENT_CHAT_DAYS.filter((day) => days[day].length > 0).map((day) => ({ day, chats: days[day] }));
}

/** A chat's status in words with the tone of its dot; `t` has `status.<status>` (and {{applied}} of {{total}}). */
export function platformAgentChatStatus(chat, t) {
  const status = PLATFORM_AGENT_CHAT_TONES[chat?.status] ? chat.status : 'answered';
  const label =
    status === 'partial'
      ? t('status.partial', { applied: chat.applied ?? 0, total: chat.total ?? 0 })
      : t(`status.${status}`);

  return { label, tone: PLATFORM_AGENT_CHAT_TONES[status] };
}

/** Whether the person (§2 `person.manager_id`) started the chat. */
export function isOwnPlatformAgentChat(chat, managerId) {
  const startedBy = chat?.started_by?.manager_id;

  return startedBy != null && managerId != null && String(startedBy) === String(managerId);
}

/**
 * Who started a chat, as the list shows it (design: "You", "Jonas Becker · FoodAmigos", "Lena Hoffmann · Owner");
 * `t` has `who.you` and `who.kinds.<kind>`.
 */
export function platformAgentChatStarter(chat, managerId, t) {
  if (isOwnPlatformAgentChat(chat, managerId)) {
    return t('who.you');
  }

  const { name, kind } = chat?.started_by ?? {};
  const kindLabel = PLATFORM_AGENT_CHAT_KINDS.includes(kind) ? t(`who.kinds.${kind}`) : null;

  return [name, kindLabel].filter(Boolean).join(' · ');
}

/** The Amigo log on this restaurant (D8): its requests filtered by the company or the brand. */
export function platformAgentLogHref(subject) {
  if (!subject?.type || subject.id == null) {
    return ScreenEnum.PlatformAgentLog;
  }

  const filter = subject.type === 'franchise' ? 'franchise_id' : 'company_id';

  return `${ScreenEnum.PlatformAgentLog}?${filter}=${encodeURIComponent(subject.id)}`;
}

/**
 * How the chat the panel shows starts eve (useEveAgent's initialSession, initialEvents, resume):
 * - kept in the slice (it was open before): its session cursor and the events it rendered, resumed from there;
 * - a chat of the agent the panel hasn't rendered yet (from the list, or a new one): its eve session from the start
 *   of its stream, replayed when it has history (`chat.resume`). eve drops what it already applied by event id;
 *   the agent's archive (GET /chats/{id}) leaves out the streamed text deltas, so it isn't a prefix eve can go on from;
 * - no session yet: eve creates one with the first message.
 */
export function platformAgentChatStart(chat, saved) {
  if (chat?.id && saved?.session?.sessionId === chat.id) {
    return { initialSession: saved.session, initialEvents: saved.events, resume: true };
  }

  if (chat?.id) {
    return { initialSession: { sessionId: chat.id, streamIndex: 0 }, initialEvents: undefined, resume: !!chat.resume };
  }

  return { initialSession: undefined, initialEvents: undefined, resume: false };
}

/** When each turn of a chat began: its first stream event's time (eve's `meta.at`), by turn id. */
export function platformAgentTurnTimes(events) {
  const times = {};

  (events ?? []).forEach((event) => {
    const turnId = event?.data?.turnId;
    const at = event?.meta?.at;

    if (turnId && at && !(turnId in times)) {
      times[turnId] = at;
    }
  });

  return times;
}

/**
 * The chat's blocks with a day line (`{ kind: 'day', id, at }`) before the first block of each day, only when the
 * chat goes on over several days (design: Day line). A block without a time yet (a message just sent) is now. A chat
 * the person can carry on (`goesOn`) whose last day was before today ends with a "Today" line (`at` null).
 */
export function withPlatformAgentDayLines(blocks, times, { now = Date.now(), timeZone, goesOn = true } = {}) {
  const today = platformAgentDayKey(now, timeZone);
  const dated = blocks.map((block) => {
    const at = (block.turnId && times?.[block.turnId]) || null;

    return { block, at, key: at ? platformAgentDayKey(at, timeZone) : today };
  });

  if (dated.every(({ key }) => key >= today)) {
    return blocks;
  }

  const result = [];
  let last = null;

  dated.forEach(({ block, at, key }) => {
    if (key !== last) {
      result.push({ kind: 'day', id: `day:${key}`, at: at ?? new Date(now).toISOString() });
      last = key;
    }

    result.push(block);
  });

  if (goesOn && last < today) {
    result.push({ kind: 'day', id: 'day:today', at: null });
  }

  return result;
}
